import { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import api from '../../services/api';
import { useLiveReload } from '../common/useLiveReload';
import { withReturnTo } from '../common/returnPath';
import { useTopicWorkspace } from '../../contexts/TopicWorkspaceContext';
import { useWebSocket } from '../../contexts/WebSocketContext';
import { useInfiniteScroll, LoadingMore, EndOfList } from '../../hooks/useInfiniteScroll';
import { isNestedSiteType, previewPath } from '../common/pathRules';
import type {
  ManagedPage,
  ManagedSite,
  DeploymentPlan,
  PageBuildStatus,
  PageStyle,
  WebSocketEvent,
} from '../../types';

const PAGE_SIZE = 25;

const STATUS_OPTIONS: { value: PageBuildStatus | ''; label: string }[] = [
  { value: '', label: 'All Statuses' },
  { value: 'DRAFT', label: 'Draft' },
  { value: 'PENDING', label: 'Pending' },
  { value: 'QUEUED', label: 'Queued' },
  { value: 'IN_PROGRESS', label: 'In Progress' },
  { value: 'WAITING_FOR_USER_ACTION', label: 'Waiting for User' },
  { value: 'WAITING_FOR_SYSTEM_ACTION', label: 'Waiting for System' },
  { value: 'READY_TO_DEPLOY', label: 'Ready to Deploy' },
  { value: 'COMPLETED', label: 'Completed' },
  { value: 'FAILED', label: 'Failed' },
  { value: 'CANCELLED', label: 'Canceled' },
  { value: 'SKIPPED', label: 'Skipped' },
  { value: 'ERROR', label: 'Error' },
];

const PAGE_STYLES: { value: PageStyle | ''; label: string }[] = [
  { value: '', label: 'All Styles' },
  { value: 'ARTICLE', label: 'Article' },
  { value: 'GUIDE', label: 'Guide' },
  { value: 'CHECKLIST', label: 'Checklist' },
  { value: 'LISTICLE', label: 'Listicle' },
  { value: 'FAQ', label: 'FAQ' },
  { value: 'COMPARISON', label: 'Comparison' },
  { value: 'REVIEWS', label: 'Reviews' },
  { value: 'PRODUCT_PAGE', label: 'Product Page' },
  { value: 'PRICING_PAGE', label: 'Pricing Page' },
  { value: 'SUPPORT_ARTICLE', label: 'Support Article' },
  { value: 'DOCUMENTATION', label: 'Documentation' },
  { value: 'TOOL', label: 'Tool' },
  { value: 'FORUM_DISCUSSION', label: 'Forum Discussion' },
  { value: 'VIDEO', label: 'Video' },
  { value: 'TEMPLATE', label: 'Template' },
];

function getStatusColor(status: PageBuildStatus | null): string {
  switch (status) {
    case 'COMPLETED': return 'bg-green-100 dark:bg-green-900/40 text-green-700 dark:text-green-300';
    case 'IN_PROGRESS': return 'bg-yellow-100 dark:bg-yellow-900/40 text-yellow-700 dark:text-yellow-300';
    case 'WAITING_FOR_USER_ACTION': return 'bg-orange-100 dark:bg-orange-900/40 text-orange-700 dark:text-orange-300';
    case 'WAITING_FOR_SYSTEM_ACTION': return 'bg-purple-100 dark:bg-purple-900/40 text-purple-700 dark:text-purple-300';
    case 'FAILED':
    case 'ERROR': return 'bg-red-100 dark:bg-red-900/40 text-red-700 dark:text-red-300';
    case 'QUEUED': return 'bg-blue-100 dark:bg-blue-900/40 text-blue-700 dark:text-blue-300';
    case 'PENDING': return 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300';
    case 'READY_TO_DEPLOY': return 'bg-teal-100 dark:bg-teal-900/40 text-teal-700 dark:text-teal-300';
    case 'DRAFT':
    case 'CANCELLED':
    case 'SKIPPED': return 'bg-gray-100 dark:bg-gray-700 text-gray-500 dark:text-gray-400';
    default: return 'bg-gray-100 dark:bg-gray-700 text-gray-500 dark:text-gray-400';
  }
}

export default function TopicPagesPane() {
  const navigate = useNavigate();
  const location = useLocation();
  const { selectedTopic } = useTopicWorkspace();
  const { subscribe } = useWebSocket();
  const topicId = selectedTopic?.id || null;
  // The node this topic hangs under, for the "edit the category slug" link.
  // `parent_id` is the column; `category_id` is its legacy spelling and is only
  // ever a root's id, so either one answers "which node is my parent".
  const parentNodeId = selectedTopic?.parent_id ?? selectedTopic?.category_id ?? null;

  // Sites and plans for filters
  const [sites, setSites] = useState<ManagedSite[]>([]);
  const [plans, setPlans] = useState<DeploymentPlan[]>([]);
  const [loadingSites, setLoadingSites] = useState(true);

  // Pages list
  const [pages, setPages] = useState<ManagedPage[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState('');
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const [totalItems, setTotalItems] = useState(0);

  // Create new page
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [newKeyword, setNewKeyword] = useState('');
  const [creating, setCreating] = useState(false);
  const [createError, setCreateError] = useState('');

  // Filters
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState<PageBuildStatus | ''>('');
  const [styleFilter, setStyleFilter] = useState<PageStyle | ''>('');
  const [siteFilter, setSiteFilter] = useState<number | ''>('');
  const [planFilter, setPlanFilter] = useState<number | ''>('');

  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const isInitialLoad = useRef(true);

  // Fetch sites on mount
  useEffect(() => {
    const fetchSites = async () => {
      setLoadingSites(true);
      try {
        const data = await api.getManagedSites();
        setSites(data);
      } catch (err) {
        console.error('Failed to load sites:', err);
      } finally {
        setLoadingSites(false);
      }
    };
    fetchSites();
  }, []);

  // Fetch plans when site changes
  useEffect(() => {
    const fetchPlans = async () => {
      if (!siteFilter) {
        setPlans([]);
        setPlanFilter('');
        return;
      }
      try {
        const data = await api.getDeploymentPlans({ managedSiteId: siteFilter, size: 100 });
        setPlans(data.items);
        if (planFilter && !data.items.find(p => p.id === planFilter)) {
          setPlanFilter('');
        }
      } catch (err) {
        console.error('Failed to load plans:', err);
        setPlans([]);
      }
    };
    fetchPlans();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [siteFilter]);

  const fetchPages = useCallback(async (pageNum: number, append: boolean = false) => {
    if (!topicId) return;

    if (append) {
      setLoadingMore(true);
    } else {
      setLoading(true);
      setPages([]);
      setHasMore(false);
      isInitialLoad.current = true;
    }

    try {
      const data = await api.getManagedPages({
        topicId,
        status: statusFilter || undefined,
        pageStyle: styleFilter || undefined,
        managedSiteId: siteFilter || undefined,
        deploymentPlanId: planFilter || undefined,
        search: search || undefined,
        page: pageNum,
        size: PAGE_SIZE,
      });

      if (append) {
        setPages(prev => {
          const existingIds = new Set(prev.map(p => p.id));
          const newItems = data.items.filter(p => !existingIds.has(p.id));
          return [...prev, ...newItems];
        });
      } else {
        setPages(data.items);
      }

      setTotalItems(data.total);
      setHasMore(pageNum < data.pages);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load pages');
    } finally {
      setLoading(false);
      setLoadingMore(false);
      isInitialLoad.current = false;
    }
  }, [topicId, statusFilter, styleFilter, siteFilter, planFilter, search]);

  // Initial load and filter changes
  useEffect(() => {
    if (topicId) {
      setPage(1);
      scrollContainerRef.current?.scrollTo(0, 0);
      fetchPages(1, false);
    }
  }, [topicId, statusFilter, styleFilter, siteFilter, planFilter, search, fetchPages]);

  const loadMore = useCallback(() => {
    if (isInitialLoad.current) return;
    if (!loadingMore && hasMore) {
      const nextPage = page + 1;
      setPage(nextPage);
      fetchPages(nextPage, true);
    }
  }, [loadingMore, hasMore, page, fetchPages]);

  useInfiniteScroll({
    onLoadMore: loadMore,
    hasMore,
    isLoading: loading || loadingMore,
  });

  // Live status badges: when a `page_build` event arrives for a page
  // currently shown in this list, its DB status changed — refetch the
  // first page so the badge re-renders. `event.status` is a websocket
  // lifecycle value (not the DB enum), so we refetch rather than patch.
  useEffect(() => {
    return subscribe((event: WebSocketEvent) => {
      if (event.event_type !== 'page_build') return;
      if (pages.some((p) => p.id === event.page_id)) {
        setPage(1);
        fetchPages(1, false);
      }
    });
  }, [subscribe, pages, fetchPages]);
  // NEW PAGES TOO. The check above only hears pages already listed, so a page
  // a bulk build or a growth session created never joined the list. A build
  // starting on a page we do not have, or a session ending, re-reads it.
  useLiveReload(
    (e) =>
      (e?.event_type === 'page_build' && e.status === 'started' && !pages.some((p) => p.id === e.page_id)) ||
      (e?.event_type === 'growth_agent_llm' && e.status === 'session_complete'),
    () => {
      setPage(1);
      fetchPages(1, false);
    },
    3_000,
  );

  /**
   * EVERY EXIT FROM THIS TABLE CARRIES A TRAIL. One helper, because six links
   * left here and only the preview remembered to stamp one.
   *
   * What that cost: the slug links landed on a node's settings, whose own back
   * button is STRUCTURAL — it goes to the node's parent — so "back" jumped you
   * UP the tree instead of returning you to the row you clicked. The page,
   * site and plan links landed on screens with no back button at all, and the
   * only way out was the browser's, which stops working the moment you edit
   * something. Which is what you came to do.
   *
   * The full current URL, not just the pathname: this pane lives at
   * `?node=<id>&tab=outputs`, and returning to the bare subject would drop you
   * at its root with the table you were reading gone.
   */
  const backHere = (to: string): string =>
    withReturnTo(to, `${location.pathname}${location.search}`, 'the subject');

  const handleEditPage = (pageItem: ManagedPage) => {
    // EditPage is gone — every "edit/view this page" entry-point lands on
    // the phased page builder now.
    navigate(backHere(`/page-builder/${pageItem.id}`));
  };

  // Deployment Targets lives at Settings ▸ Page Generation ▸ Deployment Targets.
  const handleSiteClick = (e: React.MouseEvent, siteId: number | null) => {
    e.stopPropagation();
    if (siteId) {
      navigate(backHere(`/deliver?site=${siteId}`));
    }
  };

  const handlePlanClick = (e: React.MouseEvent, siteId: number | null, planId: number | null) => {
    e.stopPropagation();
    if (siteId && planId) {
      navigate(backHere(`/deliver?site=${siteId}&plan=${planId}`));
    }
  };

  /**
   * Where to READ this page as the site it deploys to will publish it.
   *
   * The same link the Pages workspace carries — this pane is the other table
   * of the same rows, and a door that exists on only one of them is a door
   * most people never find.
   */
  const previewHref = (pageItem: ManagedPage): string | null => {
    if (!pageItem.managed_site_id || !pageItem.deployment_plan_id) return null;
    if (!isNestedSiteType(pageItem.managed_site_type)) return null;
    const path = previewPath(pageItem.path_segments ?? [], pageItem.slug ?? '', true);
    if (path === '/') return null;
    // Carry the trail, so the preview comes back to this pane rather than to
    // the site designer — a section this table has never been in.
    return backHere(
      `/deliver/${pageItem.managed_site_id}/design/preview` +
        `?plan=${pageItem.deployment_plan_id}&ready_only=1&path=${encodeURIComponent(path)}`,
    );
  };

  const clearFilters = () => {
    setStatusFilter('');
    setStyleFilter('');
    setSiteFilter('');
    setPlanFilter('');
    setSearch('');
  };

  const handleCreatePage = async () => {
    if (!topicId || !newKeyword.trim()) return;
    setCreating(true);
    setCreateError('');

    try {
      let topicKeywordId: number;

      try {
        // Step 1: Create keyword + topic_keyword link
        const kwResp = await api.createKeyword({ value: newKeyword.trim(), topic_id: topicId });
        topicKeywordId = kwResp.topic_keyword.id;
      } catch (kwErr: unknown) {
        // If 400 "already linked", look up the existing topic_keyword
        const errMsg = kwErr instanceof Error ? kwErr.message : String(kwErr);
        if (errMsg.includes('already linked') || errMsg.includes('already exists')) {
          const tkResp = await api.getTopicKeywords(topicId, null, 1, 10, 'keyword', 'asc', false, false, newKeyword.trim());
          const match = tkResp.items.find(
            (tk) => tk.keyword_value.toLowerCase() === newKeyword.trim().toLowerCase()
          );
          if (!match) {
            throw new Error('Keyword exists but could not find topic_keyword link');
          }
          topicKeywordId = match.id;
        } else {
          throw kwErr;
        }
      }

      // Step 2: Create managed page from topic_keyword
      const page = await api.createManagedPage({ topic_keyword_id: topicKeywordId });

      // Step 3: Reset form, refresh list, navigate to edit
      setShowCreateForm(false);
      setNewKeyword('');
      fetchPages(1, false);
      navigate(backHere(`/page-builder/${page.id}`));
    } catch (err: unknown) {
      setCreateError(err instanceof Error ? err.message : 'Failed to create page');
    } finally {
      setCreating(false);
    }
  };

  const searchHasValue = search.trim().length > 0;
  const hasFilters = statusFilter || styleFilter || siteFilter || planFilter || search;

  if (!topicId) {
    return (
      <div className="flex-1 flex items-center justify-center text-gray-500 dark:text-gray-400 p-8">
        Select a topic to view pages
      </div>
    );
  }

  return (
    <div className="flex-1 flex flex-col min-h-0 overflow-hidden">
      {/* Filters */}
      <div className="px-3 sm:px-6 py-3 border-b border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800 flex-shrink-0">
        <div className="flex items-center justify-between mb-2">
          <span className="text-sm text-gray-600 dark:text-gray-300">
            {totalItems} page{totalItems !== 1 ? 's' : ''}
          </span>
          <div className="flex items-center gap-3">
            {hasFilters && (
              <button
                onClick={clearFilters}
                className="text-xs text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300"
              >
                Clear filters
              </button>
            )}
            <button
              onClick={() => { setShowCreateForm(f => !f); setCreateError(''); }}
              className="text-xs font-medium text-purple-600 dark:text-purple-400 hover:text-purple-800 dark:hover:text-purple-300"
            >
              + New Page
            </button>
          </div>
        </div>
        <div className="grid grid-cols-2 sm:grid-cols-5 gap-2">
          <div className="col-span-2 sm:col-span-1">
            <input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search..."
              className={`w-full px-2 py-1.5 text-sm border rounded-md focus:outline-none ${
                searchHasValue
                  ? 'border-purple-300 dark:border-purple-700 ring-1 ring-purple-200'
                  : 'border-gray-300 dark:border-gray-600 focus:ring-1 focus:ring-purple-500'
              }`}
            />
          </div>
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value as PageBuildStatus | '')}
            className="px-2 py-1.5 text-sm border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-1 focus:ring-purple-500"
          >
            {STATUS_OPTIONS.map((o) => (
              <option key={o.value} value={o.value}>{o.label}</option>
            ))}
          </select>
          <select
            value={styleFilter}
            onChange={(e) => setStyleFilter(e.target.value as PageStyle | '')}
            className="px-2 py-1.5 text-sm border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-1 focus:ring-purple-500"
          >
            {PAGE_STYLES.map((s) => (
              <option key={s.value} value={s.value}>{s.label}</option>
            ))}
          </select>
          <select
            value={siteFilter}
            onChange={(e) => setSiteFilter(e.target.value ? Number(e.target.value) : '')}
            className="px-2 py-1.5 text-sm border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-1 focus:ring-purple-500"
            disabled={loadingSites}
          >
            <option value="">All Sites</option>
            {sites.map((site) => (
              <option key={site.id} value={site.id}>{site.name}</option>
            ))}
          </select>
          <select
            value={planFilter}
            onChange={(e) => setPlanFilter(e.target.value ? Number(e.target.value) : '')}
            className="px-2 py-1.5 text-sm border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-1 focus:ring-purple-500"
            disabled={!siteFilter}
          >
            <option value="">{siteFilter ? 'All Plans' : 'Select site'}</option>
            {plans.map((plan) => (
              <option key={plan.id} value={plan.id}>{plan.name}</option>
            ))}
          </select>
        </div>
      </div>

      {/* Create New Page Form */}
      {showCreateForm && (
        <div className="mx-3 sm:mx-6 mt-3 p-3 bg-purple-50 dark:bg-purple-900/30 border border-purple-200 dark:border-purple-800 rounded-md flex-shrink-0">
          <div className="flex items-center gap-2">
            <input
              type="text"
              value={newKeyword}
              onChange={(e) => setNewKeyword(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter' && newKeyword.trim()) handleCreatePage(); }}
              placeholder="Enter primary keyword..."
              className="flex-1 px-3 py-1.5 text-sm border border-purple-300 dark:border-purple-700 rounded-md focus:outline-none focus:ring-1 focus:ring-purple-500"
              disabled={creating}
              autoFocus
            />
            <button
              onClick={handleCreatePage}
              disabled={creating || !newKeyword.trim()}
              className="px-3 py-1.5 text-sm font-medium text-white bg-purple-600 rounded-md hover:bg-purple-700 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {creating ? 'Creating...' : 'Create'}
            </button>
            <button
              onClick={() => { setShowCreateForm(false); setNewKeyword(''); setCreateError(''); }}
              disabled={creating}
              className="px-3 py-1.5 text-sm text-gray-600 dark:text-gray-300 hover:text-gray-800 dark:hover:text-gray-200 disabled:opacity-50"
            >
              Cancel
            </button>
          </div>
          {createError && (
            <p className="mt-2 text-sm text-red-600 dark:text-red-400">{createError}</p>
          )}
        </div>
      )}

      {/* Error */}
      {error && (
        <div className="mx-3 sm:mx-6 mt-3 bg-red-50 dark:bg-red-900/30 border border-red-200 dark:border-red-800 text-red-700 dark:text-red-300 px-4 py-3 rounded text-sm">
          {error}
        </div>
      )}

      {/* Pages List */}
      {loading && pages.length === 0 ? (
        <div className="flex-1 flex items-center justify-center text-gray-500 dark:text-gray-400">
          Loading pages...
        </div>
      ) : pages.length === 0 ? (
        <div className="flex-1 flex items-center justify-center text-gray-500 dark:text-gray-400 p-8 text-center">
          {hasFilters
            ? 'No pages match your filters.'
            : 'No managed pages for this topic yet.'}
        </div>
      ) : (
        <div
          ref={scrollContainerRef}
          className="flex-1 overflow-y-auto"
        >
          {/* Mobile Card View */}
          <div className="sm:hidden divide-y divide-gray-200 dark:divide-gray-700">
            {pages.map((pageItem) => (
              <div
                key={pageItem.id}
                onClick={() => handleEditPage(pageItem)}
                className="px-3 py-3 hover:bg-gray-50 dark:hover:bg-gray-800 active:bg-gray-100 dark:active:bg-gray-700 cursor-pointer"
              >
                <div className="flex items-start justify-between gap-2">
                  <div className="min-w-0 flex-1">
                    <p className="text-sm font-medium text-purple-600 dark:text-purple-400 truncate">
                      {pageItem.keyword_value}
                    </p>
                    {pageItem.title && (
                      <p className="text-xs text-gray-500 dark:text-gray-400 truncate mt-0.5">
                        {pageItem.title}
                      </p>
                    )}
                  </div>
                  <span className={`flex-shrink-0 px-2 py-0.5 text-xs font-medium rounded ${getStatusColor(pageItem.status)}`}>
                    {STATUS_OPTIONS.find(s => s.value === pageItem.status)?.label || pageItem.status || 'Pending'}
                  </span>
                </div>
                {(pageItem.category_slug || pageItem.topic_slug || pageItem.slug) && (
                  <p className="text-xs text-gray-400 dark:text-gray-500 font-mono mt-0.5 truncate">
                    /{pageItem.category_slug || '…'}/{pageItem.topic_slug || '…'}/{pageItem.slug || '…'}
                  </p>
                )}
                <div className="mt-2 flex items-center gap-3 text-xs text-gray-500 dark:text-gray-400">
                  <span className="px-1.5 py-0.5 bg-indigo-50 dark:bg-indigo-900/30 text-indigo-600 dark:text-indigo-400 rounded">
                    {PAGE_STYLES.find(s => s.value === pageItem.page_style)?.label || pageItem.page_style}
                  </span>
                  {pageItem.managed_site_name && (
                    <span className="truncate">{pageItem.managed_site_name}</span>
                  )}
                </div>
              </div>
            ))}
          </div>

          {/* Desktop Table View */}
          <table className="hidden sm:table w-full">
            <thead className="bg-gray-50 dark:bg-gray-800 border-b sticky top-0 z-10">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Keyword</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">URL</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Title</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Style</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Status</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Site</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Plan</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200 dark:divide-gray-700 bg-white dark:bg-gray-800">
              {pages.map((pageItem) => (
                <tr
                  key={pageItem.id}
                  onClick={() => handleEditPage(pageItem)}
                  className="hover:bg-gray-50 dark:hover:bg-gray-800 cursor-pointer"
                >
                  <td className="px-4 py-3">
                    <span className="text-sm font-medium text-purple-600 dark:text-purple-400 hover:text-purple-800 dark:hover:text-purple-300">
                      {pageItem.keyword_value}
                    </span>
                  </td>
                  <td className="px-4 py-3">
                    <span className="font-mono text-sm text-gray-500 dark:text-gray-400">
                      /
                      {/* These two used to navigate to the subject you are
                          ALREADY looking at — the category one dropping `?node=`
                          and `?tab=` on the way, so it read as being thrown up a
                          level — and no slug editor ever opened. The slug lives
                          on the node's settings screen, so that is where they
                          go. It is also the only inbound link either of those
                          routes has. */}
                      <button
                        onClick={(e) => { e.stopPropagation(); if (parentNodeId) navigate(backHere(`/categories/${parentNodeId}/settings/general`)); }}
                        disabled={!parentNodeId}
                        className="text-blue-500 hover:text-blue-700 dark:hover:text-blue-300 hover:underline disabled:no-underline disabled:text-gray-400"
                        title="Edit category slug"
                      >
                        {pageItem.category_slug || <span className="text-red-400 italic">no-slug</span>}
                      </button>
                      /
                      <button
                        onClick={(e) => { e.stopPropagation(); if (topicId) navigate(backHere(`/topics/${topicId}/settings/general`)); }}
                        disabled={!topicId}
                        className="text-blue-500 hover:text-blue-700 dark:hover:text-blue-300 hover:underline disabled:no-underline disabled:text-gray-400"
                        title="Edit topic slug"
                      >
                        {pageItem.topic_slug || <span className="text-red-400 italic">no-slug</span>}
                      </button>
                      /
                      <button
                        onClick={(e) => { e.stopPropagation(); handleEditPage(pageItem); }}
                        className="text-blue-500 hover:text-blue-700 dark:hover:text-blue-300 hover:underline"
                        title="Edit page slug"
                      >
                        {pageItem.slug || <span className="text-red-400 italic">no-slug</span>}
                      </button>
                    </span>
                  </td>
                  <td className="px-4 py-3">
                    <span className="text-sm text-gray-600 dark:text-gray-300 truncate max-w-[200px] block" title={pageItem.title || ''}>
                      {pageItem.title ? (pageItem.title.length > 40 ? pageItem.title.slice(0, 40) + '...' : pageItem.title) : '-'}
                    </span>
                    {previewHref(pageItem) && (
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          navigate(previewHref(pageItem) as string);
                        }}
                        className="text-[11px] text-gray-400 dark:text-gray-500 hover:text-blue-600 dark:hover:text-blue-400 hover:underline"
                        title="Read this page in the site it will deploy into"
                      >
                        preview ↗
                      </button>
                    )}
                  </td>
                  <td className="px-4 py-3">
                    <span className="px-2 py-0.5 text-xs font-medium bg-indigo-100 dark:bg-indigo-900/40 text-indigo-700 dark:text-indigo-300 rounded">
                      {PAGE_STYLES.find(s => s.value === pageItem.page_style)?.label || pageItem.page_style}
                    </span>
                  </td>
                  <td className="px-4 py-3">
                    <span className={`px-2 py-0.5 text-xs font-medium rounded ${getStatusColor(pageItem.status)}`}>
                      {STATUS_OPTIONS.find(s => s.value === pageItem.status)?.label || pageItem.status || 'No Status'}
                    </span>
                  </td>
                  <td className="px-4 py-3">
                    {pageItem.managed_site_name ? (
                      <button
                        onClick={(e) => handleSiteClick(e, pageItem.managed_site_id)}
                        className="text-sm text-blue-600 dark:text-blue-400 hover:text-blue-800 dark:hover:text-blue-300 hover:underline"
                      >
                        {pageItem.managed_site_name}
                      </button>
                    ) : (
                      <span className="text-sm text-gray-400 dark:text-gray-500">-</span>
                    )}
                  </td>
                  <td className="px-4 py-3">
                    {pageItem.deployment_plan_name ? (
                      <button
                        onClick={(e) => handlePlanClick(e, pageItem.managed_site_id, pageItem.deployment_plan_id)}
                        className="text-sm text-blue-600 dark:text-blue-400 hover:text-blue-800 dark:hover:text-blue-300 hover:underline"
                      >
                        {pageItem.deployment_plan_name}
                      </button>
                    ) : (
                      <span className="text-sm text-gray-400 dark:text-gray-500">-</span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          <LoadingMore isLoading={loadingMore} />
          <EndOfList show={!hasMore && !loading} itemCount={pages.length} />
        </div>
      )}
    </div>
  );
}
